import {
  Bar,
  BarChart as RBarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { useWidgetDensity } from '../grid/WidgetDensityContext'
import { Sparkline } from './Sparkline'
import { ChartAccessibleTable, ChartFrame } from './ChartFrame'
import { ChartPatternDefs, chartFill } from './ChartPatternDefs'

export interface BarSeriesDef {
  key: string
  label: string
}

export interface BarChartWrapperProps<T extends object> {
  title: string
  data: T[]
  /** The field on `T` used as the category axis (e.g. jurisdiction, fiscal year). */
  categoryKey: string
  categoryLabel: string
  series: BarSeriesDef[]
  valueFormatter?: (value: number) => string
  /** Called with the clicked category's raw row and the specific series key clicked — dashboards use this to dispatch `ADD_CROSSFILTER`. */
  onBarClick?: (row: T, seriesKey: string) => void
  height?: number
}

const COMPACT_MAX_CATEGORIES = 8
const MICRO_MAX_CATEGORIES = 4

/**
 * AC2 — Credit value by jurisdiction/FY. Also the general categorical bar tile.
 *
 * **Auto-density (IB-311 Slice 1, design-system.md §5):** `compact` drops gridlines/legend and
 * thins axis ticks, plus truncates a long category list to the top N (by the first series' value)
 * with a "+N more — view as table" footnote; `micro` collapses to a `Sparkline` + one summary stat.
 * The truncation is display-only — `ChartFrame`'s data table always renders every row.
 */
export function BarChartWrapper<T extends object>({
  title,
  data,
  categoryKey,
  categoryLabel,
  series,
  valueFormatter = (v) => String(v),
  onBarClick,
  height = 260,
}: BarChartWrapperProps<T>) {
  const { density } = useWidgetDensity()
  const asRecord = (row: T) => row as unknown as Record<string, unknown>
  const primaryKey = series[0]?.key

  const maxCategories = density === 'micro' ? MICRO_MAX_CATEGORIES : density === 'compact' ? COMPACT_MAX_CATEGORIES : Infinity
  const sortedByPrimary =
    primaryKey && Number.isFinite(maxCategories)
      ? [...data].sort((a, b) => Number(asRecord(b)[primaryKey] ?? 0) - Number(asRecord(a)[primaryKey] ?? 0))
      : data
  const visible = Number.isFinite(maxCategories) ? sortedByPrimary.slice(0, maxCategories) : data
  const omittedCount = data.length - visible.length

  const table = (
    <ChartAccessibleTable
      caption={`${title} — data table`}
      columns={[
        { key: 'category', label: categoryLabel },
        ...series.map((s) => ({ key: s.key, label: s.label, align: 'end' as const })),
      ]}
      rows={data.map((row) => {
        const out: Record<string, string> = { category: String(asRecord(row)[categoryKey] ?? '') }
        for (const s of series) out[s.key] = valueFormatter(Number(asRecord(row)[s.key] ?? 0))
        return out
      })}
    />
  )

  const microFallback =
    primaryKey && data.length > 0 ? (
      <Sparkline
        points={data.map((row) => ({
          key: String(asRecord(row)[categoryKey] ?? ''),
          label: String(asRecord(row)[categoryKey] ?? ''),
          value: Number(asRecord(row)[primaryKey] ?? 0),
        }))}
        valueFormatter={valueFormatter}
      />
    ) : undefined

  const footnote =
    omittedCount > 0 ? (
      <p className="iw-chart-truncation-note">
        +{omittedCount} more {categoryLabel.toLowerCase()}
        {omittedCount === 1 ? '' : 's'} — view as a table for all.
      </p>
    ) : null

  return (
    <ChartFrame
      title={title}
      height={height}
      table={table}
      microFallback={microFallback}
      footnote={footnote}
      chart={
        <ResponsiveContainer width="100%" height="100%">
          <RBarChart data={visible}>
            <ChartPatternDefs seriesCount={series.length} />
            {density === 'full' ? <CartesianGrid strokeDasharray="3 3" stroke="var(--iw-border)" /> : null}
            <XAxis
              dataKey={categoryKey}
              tick={density === 'full' ? { fill: 'var(--iw-text-muted)', fontSize: 12 } : false}
              tickLine={density === 'full'}
              axisLine={density !== 'micro'}
            />
            <YAxis
              tick={density === 'full' ? { fill: 'var(--iw-text-muted)', fontSize: 12 } : false}
              tickFormatter={valueFormatter}
              width={density === 'full' ? 60 : 0}
              axisLine={density !== 'micro'}
              tickLine={density === 'full'}
            />
            <Tooltip formatter={(value) => valueFormatter(Number(value))} />
            {series.length > 1 && density === 'full' ? <Legend /> : null}
            {series.map((s, i) => (
              <Bar
                key={s.key}
                dataKey={s.key}
                name={s.label}
                fill={chartFill(i)}
                cursor={onBarClick ? 'pointer' : undefined}
                onClick={(entry) => onBarClick?.((entry as { payload: T }).payload, s.key)}
              />
            ))}
          </RBarChart>
        </ResponsiveContainer>
      }
    />
  )
}
